/**
 * Panel de administración: el membrete de los documentos.
 *
 * Es la primera pantalla de la plataforma que ESCRIBE configuración, y lo que
 * escribe acaba impreso en documentos que alguien firma. De ahí dos decisiones
 * visibles aquí: cada guardado crea una revisión nueva —nada se sobrescribe— y
 * el historial de quién cambió qué se muestra en la misma página, no escondido
 * en una pestaña aparte.
 *
 * Lo que este panel NO hace es tocar el mapping contable. `mapping:write` es del
 * contador y sigue siendo un cambio versionado con aprobación nominal: el
 * administrador administra quién firma, no firma.
 */

import { useState } from "react";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import type { Appearance, DocumentInk, Identity, Signatory } from "../api/types";
import { ErrorState, Loading } from "../components/States";
import { aplicarApariencia, reparo } from "../components/apariencia";
import { formatDate } from "../components/format";

/** Tope del logo, el mismo que impone el servidor. */
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

interface Borrador {
  company: string;
  short_name: string;
  title: string;
  currency_note: string;
  legal_note: string;
  signatories: Signatory[];
}

function borradorDe(identity: Identity): Borrador {
  return {
    company: identity.company,
    short_name: identity.short_name,
    title: identity.title,
    currency_note: identity.currency_note,
    legal_note: identity.legal_note,
    signatories: identity.signatories.map((firma) => ({ ...firma })),
  };
}

export function Settings() {
  const { data, error, loading, reload } = useAsync(() => api.identity(), []);
  const [borrador, setBorrador] = useState<Borrador | null>(null);
  const [cargado, setCargado] = useState<number | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  // El borrador se siembra al llegar los datos y cuando cambia la revisión, sin
  // pasar por un efecto: ajustar estado durante el render es lo que React
  // recomienda para derivar de props, y evita el parpadeo del formulario vacío.
  if (data && cargado !== data.revision) {
    setCargado(data.revision);
    setBorrador(borradorDe(data));
  }

  if (loading && !data) return <Loading what="la configuración" />;
  if (error) return <ErrorState error={error} />;
  if (!data || !borrador) return null;

  const editar = (campo: keyof Borrador, valor: string) =>
    setBorrador({ ...borrador, [campo]: valor });

  const editarFirma = (indice: number, campo: keyof Signatory, valor: string) =>
    setBorrador({
      ...borrador,
      signatories: borrador.signatories.map((firma, i) =>
        i === indice ? { ...firma, [campo]: valor } : firma,
      ),
    });

  async function guardar() {
    if (!borrador) return;
    setGuardando(true);
    setAviso(null);
    setFallo(null);
    try {
      const resultado = await api.saveIdentity(borrador);
      setAviso(`Guardado como revisión ${resultado.revision}.`);
      reload();
    } catch (err) {
      setFallo(err instanceof Error ? err.message : String(err));
    } finally {
      setGuardando(false);
    }
  }

  async function subirLogo(archivo: File) {
    setFallo(null);
    setAviso(null);
    if (archivo.size > MAX_LOGO_BYTES) {
      setFallo(
        `El logo pesa ${Math.round(archivo.size / 1024)} KB y el máximo son ${
          MAX_LOGO_BYTES / 1024
        } KB.`,
      );
      return;
    }
    // `leerBase64` estaba fuera del try. `FileReader` rechaza con un archivo
    // ilegible, y el llamador invoca `void subirLogo(...)`: el rechazo no lo
    // capturaba nadie, así que el usuario no veía ningún error y el navegador
    // registraba una promesa rechazada sin manejar.
    try {
      const base64 = await leerBase64(archivo);
      await api.saveLogo(base64, archivo.type);
      setAviso("Logo actualizado.");
      reload();
    } catch (err) {
      setFallo(err instanceof Error ? err.message : String(err));
    }
  }

  async function quitarLogo() {
    setFallo(null);
    try {
      await api.removeLogo();
      setAviso("Logo retirado.");
      reload();
    } catch (err) {
      setFallo(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Membrete de los documentos</h1>
          <p>
            Razón social, nota de moneda, firmantes y logo. Aparecen en cada PDF y
            cada XLSX que la plataforma entrega. Un cambio aquí crea una revisión
            nueva y queda registrado con su autor: nada se sobrescribe.
          </p>
        </div>
      </div>

      {data.is_seed && (
        <p className="note">
          Todavía no se ha guardado ninguna revisión desde el panel: los documentos
          salen con la semilla versionada en el repositorio.
        </p>
      )}
      {aviso && <p className="note ok">{aviso}</p>}
      {fallo && <p className="note bad">{fallo}</p>}

      <section className="card">
        <h2>Datos de la empresa</h2>
        <div className="field">
          <label htmlFor="company">Razón social</label>
          <input
            id="company"
            value={borrador.company}
            onChange={(e) => editar("company", e.target.value)}
          />
          <span className="small muted">
            Encabeza todos los documentos. Es el nombre legal completo, no el de la
            base de datos.
          </span>
        </div>
        <div className="field">
          <label htmlFor="short_name">Nombre corto</label>
          <input
            id="short_name"
            value={borrador.short_name}
            onChange={(e) => editar("short_name", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="title">Título del paquete</label>
          <input
            id="title"
            value={borrador.title}
            onChange={(e) => editar("title", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="currency_note">Nota de moneda</label>
          <input
            id="currency_note"
            value={borrador.currency_note}
            onChange={(e) => editar("currency_note", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="legal_note">Nota legal</label>
          <textarea
            id="legal_note"
            rows={3}
            value={borrador.legal_note}
            onChange={(e) => editar("legal_note", e.target.value)}
          />
        </div>
      </section>

      <section className="card">
        <h2>Firmantes</h2>
        <p className="small muted">
          En el documento el orden dentro de cada bloque es nombre, inscripción y
          cargo: el número de inscripción acompaña al nombre.
        </p>
        {borrador.signatories.map((firma, indice) => (
          <div className="firma-fila" key={indice}>
            <div className="field">
              <label htmlFor={`nombre-${indice}`}>Nombre</label>
              <input
                id={`nombre-${indice}`}
                value={firma.name}
                onChange={(e) => editarFirma(indice, "name", e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor={`cargo-${indice}`}>Cargo</label>
              <input
                id={`cargo-${indice}`}
                value={firma.role}
                onChange={(e) => editarFirma(indice, "role", e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor={`insc-${indice}`}>Inscripción</label>
              <input
                id={`insc-${indice}`}
                value={firma.registration ?? ""}
                onChange={(e) =>
                  editarFirma(indice, "registration", e.target.value)
                }
              />
            </div>
            <button
              type="button"
              onClick={() =>
                setBorrador({
                  ...borrador,
                  signatories: borrador.signatories.filter((_, i) => i !== indice),
                })
              }
            >
              Quitar
            </button>
          </div>
        ))}
        {borrador.signatories.length < 4 && (
          <button
            type="button"
            onClick={() =>
              setBorrador({
                ...borrador,
                signatories: [
                  ...borrador.signatories,
                  { name: "", role: "", registration: "" },
                ],
              })
            }
          >
            Añadir firmante
          </button>
        )}
      </section>

      <section className="card">
        <h2>Logo</h2>
        <p className="small muted">
          PNG o JPEG, hasta 2 MB. Se incrusta en cada página de cada PDF, así que
          conviene el archivo más pequeño que se vea bien.
        </p>
        {data.has_logo ? (
          <div className="logo-preview">
            <img src={api.logoUrl(data.revision)} alt="Logo de la empresa" />
            <span className="small muted">
              {Math.round(data.logo_bytes / 1024)} KB
            </span>
            <button type="button" onClick={quitarLogo}>
              Quitar logo
            </button>
          </div>
        ) : (
          <p className="small muted">No hay logo configurado.</p>
        )}
        <input
          type="file"
          accept="image/png,image/jpeg"
          onChange={(e) => {
            const archivo = e.target.files?.[0];
            if (archivo) void subirLogo(archivo);
          }}
        />
      </section>

      <div className="toolbar">
        <button type="button" className="btn" disabled={guardando} onClick={guardar}>
          {guardando ? "Guardando…" : "Guardar revisión"}
        </button>
        <div className="spacer" />
        <span className="small muted">
          Revisión vigente: {data.is_seed ? "semilla del repositorio" : data.revision}
        </span>
      </div>

      <section className="card">
        <h2>Historial</h2>
        {data.history.length === 0 ? (
          <p className="small muted">Todavía no hay revisiones guardadas.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Revisión</th>
                  <th>Autor</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((fila) => (
                  <tr key={fila.revision}>
                    <td>{fila.revision}</td>
                    <td>{fila.updated_by}</td>
                    <td>{formatDate(fila.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* El horario va DESPUÉS del membrete y su historial, no antes: lo que un
          administrador entra a cambiar casi siempre es quién firma. */}
      <ScheduleCard />

      <AppearanceCard />
    </>
  );
}

/** El archivo, ya en base64 y sin el prefijo `data:`. */
function leerBase64(archivo: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onerror = () => reject(new Error("no se pudo leer el archivo"));
    lector.onload = () => {
      const resultado = String(lector.result ?? "");
      resolve(resultado.slice(resultado.indexOf(",") + 1));
    };
    lector.readAsDataURL(archivo);
  });
}

/**
 * Horario de sincronización automática.
 *
 * La sincronización sigue siendo manual: el botón está donde estaba. Esto es lo
 * que además permite dejarla corriendo sola, por ejemplo todos los días a
 * medianoche.
 *
 * Vive en el panel y no en la infraestructura por una razón concreta: cambiar a
 * qué hora se actualiza el cierre es una decisión de operación contable. Si
 * estuviera en Terraform, moverla de medianoche a las seis exigiría un cambio de
 * código y un despliegue, y quien toma esa decisión no es quien opera la nube.
 */
export function ScheduleCard() {
  const { data, error, loading, reload } = useAsync(() => api.schedule(), []);
  const [horas, setHoras] = useState<string[] | null>(null);
  const [activo, setActivo] = useState<boolean | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  if (loading) return <Loading what="el horario" />;
  if (error) return <ErrorState error={error} />;
  if (!data) return null;

  const horasActuales = horas ?? data.hours;
  const activoActual = activo ?? data.enabled;
  const paso = data.interval_minutes;

  // Solo se ofrecen las horas que el disparador puede atender. Un desplegable
  // con «00:07» prometería una precisión que no existe: el disparo llega cada
  // quince minutos, así que esa hora se atendería a las 00:15 y el historial
  // contradiría al panel.
  const opciones: string[] = [];
  for (let minuto = 0; minuto < 24 * 60; minuto += paso) {
    opciones.push(
      `${String(Math.floor(minuto / 60)).padStart(2, "0")}:${String(minuto % 60).padStart(2, "0")}`,
    );
  }

  async function guardar() {
    setGuardando(true);
    setAviso(null);
    try {
      const guardado = await api.saveSchedule({
        enabled: activoActual,
        hours: horasActuales,
      });
      setAviso(`Guardado como revisión ${guardado.revision}.`);
      setHoras(null);
      setActivo(null);
      reload();
    } catch (e) {
      setAviso(e instanceof Error ? e.message : "no se pudo guardar");
    } finally {
      setGuardando(false);
    }
  }

  return (
    <section className="card">
      <h2>Sincronización automática</h2>
      <p className="nota">
        La sincronización manual sigue disponible en cualquier momento. Esto añade
        las horas a las que se ejecuta sola, en hora de {data.timezone.split("/")[1]?.replace("_", " ")}.
        Cada corrida tarda unos cuatro minutos.
      </p>

      <label className="check">
        <input
          type="checkbox"
          checked={activoActual}
          onChange={(e) => setActivo(e.target.checked)}
        />
        Ejecutar automáticamente
      </label>

      <div className="horario">
        {horasActuales.map((hora, indice) => (
          <div className="horario-fila" key={`${hora}-${indice}`}>
            <select
              value={hora}
              disabled={!activoActual}
              onChange={(e) => {
                const siguiente = [...horasActuales];
                siguiente[indice] = e.target.value;
                setHoras(siguiente);
              }}
            >
              {opciones.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-sutil"
              disabled={!activoActual}
              onClick={() => setHoras(horasActuales.filter((_, i) => i !== indice))}
            >
              Quitar
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn-sutil"
          disabled={!activoActual || horasActuales.length >= data.max_hours}
          onClick={() => setHoras([...horasActuales, "00:00"])}
        >
          Añadir una hora
        </button>
      </div>

      <div className="acciones">
        <button type="button" onClick={guardar} disabled={guardando}>
          {guardando ? "Guardando…" : "Guardar horario"}
        </button>
        {aviso && <span className="nota">{aviso}</span>}
      </div>

      {/* Quién lo cambió y cuándo, como en el membrete: la tabla es
          append-only y el historial es parte del control. */}
      {data.updated_by && (
        <p className="nota">
          Revisión {data.revision}, guardada por {data.updated_by}
          {data.updated_at ? ` el ${formatDate(data.updated_at)}` : ""}.
        </p>
      )}
    </section>
  );
}


/** Rótulos de los ajustes preparados. Un `preset` desconocido sale tal cual. */
const AJUSTES: Record<string, { titulo: string; nota: string }> = {
  claro: {
    titulo: "Claro",
    nota: "Cabecera gris clara con filete, como los reportes de Odoo. Es el ajuste de fábrica.",
  },
  grafito: {
    titulo: "Grafito",
    nota: "Cabecera oscura en gris frío, solo para los libros.",
  },
  navy: {
    titulo: "Navy",
    nota: "El azul institucional de siempre, ahora solo en los libros.",
  },
  pizarra: {
    titulo: "Pizarra",
    nota: "Cabecera clara con sesgo azul y filete oscuro. El más institucional.",
  },
  tinta: {
    titulo: "Tinta",
    nota: "Cabecera casi blanca. La separación la hace el filete, no el fondo.",
  },
};

/** Los cuatro colores ajustables, en el orden en que se ven en pantalla. */
const CAMPOS: { campo: keyof Appearance; rotulo: string }[] = [
  { campo: "page_bg", rotulo: "Fondo de la página" },
  { campo: "table_header", rotulo: "Cabecera de tabla" },
  { campo: "table_zebra", rotulo: "Sobrevuelo de fila" },
  { campo: "structure", rotulo: "Estructura" },
];

/**
 * Las cinco tintas del entregable, en el orden en que se leen: de la más
 * oscura a la más clara. Ese orden no es cosmético — el servidor lo exige.
 */
const CAMPOS_DOC: { campo: keyof DocumentInk; rotulo: string; nota: string }[] = [
  { campo: "doc_rotulo", rotulo: "Rótulos y totales", nota: "razón social, títulos, totales y sus filetes" },
  { campo: "doc_cuerpo", rotulo: "Cuerpo", nota: "el texto de las filas" },
  { campo: "doc_dato", rotulo: "Cifras", nota: "los importes corrientes" },
  { campo: "doc_segundo", rotulo: "Secundaria", nota: "moneda, cabeceras de columna, notas al pie" },
  { campo: "doc_filete", rotulo: "Filetes", nota: "los hilos de las tablas" },
];

/**
 * Los colores de las tablas.
 *
 * Se ofrecen TRES ajustes preparados y, debajo, los colores sueltos. El orden
 * no es casual: un panel que empiece por tres selectores de color invita a
 * combinarlos, y las combinaciones ya revisadas enteras son las de arriba.
 *
 * La tinta de la cabecera no se ofrece: la deduce el servidor del fondo por
 * contraste. Poder elegir las dos permitiría dejar una cabecera ilegible, y
 * quien lo descubriría sería un contador a mitad de un cierre.
 */
export function AppearanceCard() {
  const { data, error, loading, reload } = useAsync(() => api.appearance(), []);
  const [borrador, setBorrador] = useState<Partial<Appearance> | null>(null);
  const [tinta, setTinta] = useState<Partial<DocumentInk> | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  if (loading) return <Loading what="la apariencia" />;
  if (error) return <ErrorState error={error} />;
  if (!data) return null;

  const actual = { ...data, ...borrador };
  const presets = data.presets ?? {};

  async function guardar(payload: {
    preset: string;
    page_bg?: string;
    table_header?: string;
    table_zebra?: string;
    structure?: string;
    documento?: Partial<DocumentInk>;
  }) {
    setGuardando(true);
    setAviso(null);
    setFallo(null);
    try {
      const guardada = await api.saveAppearance(payload);
      // Se aplica en el acto: cambiar un color y tener que recargar para verlo
      // convierte una decisión visual en un ensayo a ciegas.
      aplicarApariencia(guardada);
      setAviso(`Guardado como revisión ${guardada.revision}.`);
      setBorrador(null);
      setTinta(null);
      reload();
    } catch (e) {
      setFallo(e instanceof Error ? e.message : "no se pudo guardar");
    } finally {
      setGuardando(false);
    }
  }

  const editar = (campo: keyof Appearance, valor: string) =>
    setBorrador({ ...borrador, [campo]: valor, preset: "custom" });

  const hayReparos = CAMPOS.some(({ campo }) => reparo(campo, String(actual[campo] ?? "")));
  const tintaActual = { ...data.documento, ...tinta };
  const vistaDocumento = {
    "--vd-rotulo": tintaActual.doc_rotulo,
    "--vd-cuerpo": tintaActual.doc_cuerpo,
    "--vd-dato": tintaActual.doc_dato,
    "--vd-segundo": tintaActual.doc_segundo,
    "--vd-filete": tintaActual.doc_filete,
  } as React.CSSProperties;

  return (
    <section className="card">
      <h2>Colores de las tablas</h2>
      <p className="note">
        Afecta a la cabecera de los <b>libros</b> —Diario, Mayor, Compras,
        Ventas—, a las filas alternas y al color de estructura: el botón
        principal, el isotipo y la pestaña activa.
        {" "}
        <b>No afecta a los estados financieros</b>: el Balance, el ESF y el
        Resultado Integral llevan cabecera gris y jerarquía en negrita, que es
        la presentación a la que el contador está acostumbrado. Tampoco toca el
        rojo de los negativos, el gris de los ceros ni el verde y el ámbar de
        los estados: esos significan algo.
      </p>

      <div className="ajustes">
        {Object.entries(presets).map(([nombre, colores]) => (
          <button
            type="button"
            key={nombre}
            className={actual.preset === nombre ? "ajuste elegido" : "ajuste"}
            aria-pressed={actual.preset === nombre}
            disabled={guardando}
            onClick={() => guardar({ preset: nombre })}
          >
            <span className="ajuste-muestra" aria-hidden="true">
              <i style={{ background: colores.page_bg }} />
              <i style={{ background: colores.table_header }} />
              <i style={{ background: colores.structure }} />
            </span>
            <b>{AJUSTES[nombre]?.titulo ?? nombre}</b>
            <span className="small muted">{AJUSTES[nombre]?.nota}</span>
          </button>
        ))}
      </div>

      {/* Una apariencia guardada manda sobre el ajuste de fábrica, y hasta
          ahora no había forma de saberlo: quien guardaba un color a mano dejaba
          de recibir los cambios de diseño del producto sin que nada se lo
          dijera. Ahora lo dice, y ofrece la vuelta en un botón. */}
      {actual.preset === "custom" && (
        <p className="note">
          Está usando colores propios (revisión {data.revision}). Mientras haya
          una apariencia guardada, los ajustes de fábrica no se aplican.{" "}
          <button
            type="button"
            className="link"
            disabled={guardando}
            onClick={() => guardar({ preset: "claro" })}
          >
            Volver al ajuste de fábrica
          </button>
        </p>
      )}

      <div className="colores">
        {CAMPOS.map(({ campo, rotulo }) => {
          const valor = String(actual[campo] ?? "");
          const aviso = reparo(campo, valor);
          return (
            <label className={aviso ? "color con-reparo" : "color"} key={campo}>
              <span>{rotulo}</span>
              <input
                type="color"
                value={valor}
                onChange={(e) => editar(campo, e.target.value)}
              />
              <span className="mono small muted">{valor}</span>
              {/* El aviso sale MIENTRAS se elige, no al pulsar «Guardar». La
                  validación de verdad sigue siendo la del servidor —esta solo
                  evita que el usuario descubra el límite a base de errores. */}
              {aviso && <span className="small reparo">{aviso}</span>}
            </label>
          );
        })}
      </div>

      {/* La vista previa usa una tabla de verdad, con las mismas clases: una
          muestra de color no dice si una cabecera se lee. */}
      <div
        className="table-wrap"
        style={{ marginTop: 12, background: actual.page_bg, padding: 12 }}
      >
        <table
          style={
            {
              "--th-bg": actual.table_header,
              "--fila-hover": actual.table_zebra,
            } as React.CSSProperties
          }
        >
          <thead>
            <tr>
              <th scope="col">Cuenta</th>
              <th scope="col">Nombre de cuenta</th>
              <th scope="col" className="num">
                Debe
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="mono">410204</td>
              <td>Costos de Construcciones y Mejoras</td>
              <td className="num">140,418.45</td>
            </tr>
            <tr>
              <td className="mono">210201</td>
              <td>Proveedores Locales</td>
              <td className="num">0.00</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* --- La tinta del ENTREGABLE ---------------------------------------
          Separada de los colores de tabla y debajo de ellos, porque no es lo
          mismo: arriba se ajusta cómo se ve la aplicación; aquí, cómo se
          IMPRIME lo que el contador firma. Y el documento va en grises: el
          servidor rechaza un color con demasiada saturación, así que el azul
          institucional que se retiró del PDF no puede volver por aquí. */}
      <h3 className="sub">Tinta del documento</h3>
      <p className="note">
        Las cinco tintas con las que se imprimen el PDF y el Excel del cierre.
        Van en <b>grises</b>, de la más oscura a la más clara, y el servidor lo
        exige: un documento que se firma no lleva color de marca, y una
        jerarquía invertida haría que el total se leyera más flojo que el
        detalle que resume. El rojo del descuadre y el paréntesis del negativo
        no se tocan: significan algo.
      </p>

      <div className="colores">
        {CAMPOS_DOC.map(({ campo, rotulo, nota }) => {
          const valor = String(tintaActual[campo] ?? "");
          return (
            <label className="color" key={campo}>
              <span>{rotulo}</span>
              <input
                type="color"
                value={valor}
                onChange={(e) => setTinta({ ...tinta, [campo]: e.target.value })}
              />
              <span className="mono small muted">{valor}</span>
              <span className="small muted">{nota}</span>
            </label>
          );
        })}
      </div>

      {/* La vista previa es un ESTADO, no una muestra de color: lo que hay que
          poder juzgar es si el total se distingue del detalle y si el filete
          separa sin subrayar, y eso no se ve en cinco cuadrados. */}
      <div className="vista-documento" style={vistaDocumento}>
        <div className="vd-razon">BITCOIN STRATEGIC RESERVE, S.A. DE C.V.</div>
        <div className="vd-titulo">ESTADO DE SITUACIÓN FINANCIERA</div>
        <div className="vd-fecha">AL 31 DE JULIO DE 2026</div>
        <div className="vd-regla" />
        <table className="vd-tabla">
          <thead>
            <tr>
              <th>Concepto</th>
              <th className="num">Importe</th>
            </tr>
          </thead>
          <tbody>
            <tr className="vd-seccion">
              <td>ACTIVO CORRIENTE</td>
              <td className="num">448,685,029.04</td>
            </tr>
            <tr>
              <td>Efectivo y equivalentes</td>
              <td className="num">(1,247,483.04)</td>
            </tr>
            <tr>
              <td>Cuentas y documentos por cobrar</td>
              <td className="num">40,600,000.00</td>
            </tr>
            <tr className="vd-total">
              <td>TOTAL ACTIVO</td>
              <td className="num">1,201,353,661.68</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="acciones">
        <button
          type="button"
          disabled={guardando || (borrador === null && tinta === null) || hayReparos}
          onClick={() =>
            guardar({
              // Se envía SOLO lo que se tocó. La API acepta actualizaciones
              // parciales y lo que no llega conserva su valor, así que mandar
              // las dos mitades siempre significaba reenviar la que no se editó
              // tal como estaba al CARGAR la pantalla: si otra sesión guardó
              // entretanto, este guardado la pisaba con datos viejos.
              preset: borrador ? "custom" : actual.preset,
              ...(borrador
                ? {
                    page_bg: actual.page_bg,
                    table_header: actual.table_header,
                    table_zebra: actual.table_zebra,
                    structure: actual.structure,
                  }
                : {}),
              ...(tinta ? { documento: tinta } : {}),
            })
          }
        >
          {guardando ? "Guardando…" : "Guardar colores"}
        </button>
        {(borrador || tinta) && (
          <button
            type="button"
            className="btn-sutil"
            onClick={() => {
              setBorrador(null);
              setTinta(null);
            }}
          >
            Descartar
          </button>
        )}
        {aviso && <span className="note ok">{aviso}</span>}
        {fallo && <span className="note bad">{fallo}</span>}
      </div>

      {data.updated_by && (
        <p className="note">
          Revisión {data.revision}, guardada por {data.updated_by}
          {data.updated_at ? ` el ${formatDate(data.updated_at)}` : ""}.
        </p>
      )}
    </section>
  );
}
